import axios from './axiosSetup3';

export interface Subtenant {
    subtenant_id: number;
    name: string;
    comment?: string;
    max_parking_slots: number;
    max_permits: number;
    entity_ids: number[]; // Массив привязанных парковочных мест/локаций
    invitationEmail?: string; // для email приглашения, не сохраняется в БД, только для UI
}

export interface SubtenantQuota {
    subtenant_id: number;
    entity_id: number;
    max_parking_slots: number;
    max_permits: number;
    occupied_parking_slots?: number;
    ts_update?: string;
}

export interface User {
    id: number;
    email: string;
    name: string;
    subtenant_id: number;
    is_active: boolean;
}

// 1. Получить список субарендаторов (PostgREST/бэкенд сам отфильтрует по customer_id,subtenant_id через RLS)
const get = async (): Promise<Subtenant[]> => {
    try {
        const res = await axios.get<Subtenant[]>('/lpr_subtenant');
        return res.data;
    } catch (error) {
        console.error('Error fetching subtenants:', error);
        return [];
    }
};

// 2. Создать нового субарендатора
const post = async (data: Subtenant): Promise<Subtenant> => {
    const { entity_ids, subtenant_id, invitationEmail, ...insertData } = data;
    const res = await axios.post<Subtenant[]>('/lpr_subtenant', insertData, {
        headers: {
            'Prefer': 'return=representation' // Чтобы PostgREST вернул созданный объект с subtenant_id
        }
    });
    return res.status === 201 ? res.data[0] : {} as Subtenant;
};

// 3. Обновить данные субарендатора (больше не передаем customer_id в query-параметрах)
const put = async (data: Subtenant): Promise<Subtenant> => {
    const { entity_ids, invitationEmail, ...updateData } = data;
    const res = await axios.put<Subtenant>(`/lpr_subtenant?subtenant_id=eq.${data.subtenant_id}`, updateData);
    return res.status === 204 ? data : {} as Subtenant;
};

// 4. Удалить субарендатора
const delete_ = async (id: number): Promise<void> => {
    await axios.delete(`/lpr_subtenant?subtenant_id=eq.${id}`);
};

// 5. Получить ID сущностей, которые нарезаны данному субарендатору
const getSubtenantToEntity = async (subtenant_id: number): Promise<number[]> => {
    const res = await axios.get(`/lpr_subtenant_to_entity?subtenant_id=eq.${subtenant_id}&select=entity_id`);
    return res.data.map((row: any) => String(row.entity_id));
    // return res.data.map((row: any) => row.entity_id);
};

// 6. Обновить привязку сущностей (вызываем RPC-функцию, которая сама знает customer_id из сессии)
const putSubtenantToEntity = async (subtenant_id: number, entity_ids: number[]): Promise<void> => {
    await axios.post('/rpc/sf_subtenant_to_entity_merge', {
        p_subtenant_id: subtenant_id,
        p_entity_ids: entity_ids,
    });
};

// const getIsSubtenantActive = async (subtenant_id: number): Promise<boolean> => {
//     return false; // Заглушка для разработки, пока нет реальной реализации на бэкенде


//     const res = await axios.get(`/auth_user?subtenant_id=eq.${subtenant_id}`, {
//         headers: {
//             'Accept-Profile': 'dj' // schema
//         }
//     });
//     return res.data.length > 0 ? res.data[0].is_active : false;
// }

const getSubtenantQuota = async (subtenant_id: number): Promise<SubtenantQuota[]> => {
    // const res = await axios.get(`/lpr_subtenant_to_entity?subtenant_id=eq.${subtenant_id}`);
    const res = await axios.get<(SubtenantQuota & { lpr_entity: unknown })[]>(
        `/lpr_subtenant_to_entity?select=*,lpr_entity!inner(*)&subtenant_id=eq.${subtenant_id}&lpr_entity.type=eq.1`
    );
    return res.data.map(({ lpr_entity, ...rest }) => rest);
};

interface SubtenantQuotaWithName__ extends SubtenantQuota {
  subtenant_name?: string;
}

const getQuotas__ = async (): Promise<SubtenantQuotaWithName__[]> => {
    // Запрашиваем данные, включая lpr_entity и lpr_subtenant(name)
    const res = await axios.get<(SubtenantQuota & { 
        lpr_entity: unknown; 
        lpr_subtenant: { name: string } | null; 
    })[]>(
        `/lpr_subtenant_to_entity?select=*,lpr_entity!inner(*),lpr_subtenant!inner(name)&lpr_entity.type=eq.1`
    );
    
    // Маппим данные: убираем lpr_entity и выносим name наружу как subtenant_name
    return res.data.map(({ lpr_entity, lpr_subtenant, ...rest }) => ({
        ...rest,
        subtenant_name: lpr_subtenant?.name // теперь имя доступно прямо в строке
    }));
};

// lpr_entity в запросе чтобы брать только парковки (lpr_entity.type == 1)
const getQuotas = async (): Promise<SubtenantQuota[]> => {
    const res = await axios.get<(SubtenantQuota & { lpr_entity: unknown })[]>(
        `/lpr_subtenant_to_entity?select=*,lpr_entity!inner(*)&lpr_entity.type=eq.1`
    );
    return res.data.map(({ lpr_entity, ...rest }) => rest);
};

const updateSingleQuotaRow = async (data: SubtenantQuota): Promise<boolean> => {
    const url = `/lpr_subtenant_to_entity?subtenant_id=eq.${data.subtenant_id}&entity_id=eq.${data.entity_id}`;
    // const url = `/lpr_subtenant_to_entity?and=(subtenant_id.eq.${data.subtenant_id},entity_id.eq.${data.entity_id})`;
    try {
        const res = await axios.put(url, data);
        return 200 <= res.status && res.status < 300;
    } catch (error) {
        console.error("Ошибка при обновлении квоты:", error);
        return false;
    }
};

export const subtenantApi = {
    get,
    post,
    put,
    delete: delete_,
    getSubtenantToEntity,
    putSubtenantToEntity,
    // getIsSubtenantActive,
    getSubtenantQuota,
    updateSingleQuotaRow,
    getQuotas,
};